"use client";

import type { Account } from "../../data/accounts";
type VatMode = "NORMAL" | "PAUSCHAL" | "NICHT_STEUERPFLICHTIG";

type GroupRow = {
  kind: "group";
  key: string;
  code: string;
  label: string;
  level: 1 | 2 | 3;
};

type AccountRow = {
  kind: "account";
  account: Account;
};

type Row = GroupRow | AccountRow;

const CLASS_LABELS: Record<string, string> = {
  "1": "Aktiven",
  "2": "Passiven",
  "3": "Betrieblicher Ertrag aus Lieferungen und Leistungen",
  "4": "Aufwand für Material, Handelswaren, Dienstleistungen und Energie",
  "5": "Personalaufwand",
  "6": "Übriger betrieblicher Aufwand, Abschreibungen und Wertberichtigungen sowie Finanzergebnis",
  "7": "Betrieblicher Nebenerfolg",
  "8": "Betriebsfremder, ausserordentlicher, einmaliger oder periodenfremder Aufwand und Ertrag",
  "9": "Abschluss"
};

const GROUP_2_LABELS: Record<string, string> = {
  "10": "Umlaufvermögen",
  "11": "Forderungen",
  "12": "Vorräte und nicht fakturierte Dienstleistungen",
  "13": "Aktive Rechnungsabgrenzungen",
  "14": "Anlagevermögen",
  "18": "Nicht einbezahltes Kapital",
  "20": "Kurzfristiges Fremdkapital",
  "24": "Langfristiges Fremdkapital",
  "25": "Übrige langfristige Verbindlichkeiten",
  "26": "Rückstellungen",
  "28": "Eigenkapital",
  "29": "Reserven und Jahresergebnis",
  "30": "Betrieblicher Ertrag",
  "40": "Aufwand Material/Handelswaren/Dienstleistungen",
  "50": "Personalaufwand",
  "60": "Übriger betrieblicher Aufwand",
  "70": "Betrieblicher Nebenerfolg",
  "80": "Betriebsfremder/Ausserordentlicher Aufwand und Ertrag",
  "90": "Abschluss"
};

const GROUP_3_LABELS: Record<string, string> = {
  "100": "Flüssige Mittel",
  "106": "Kurzfristig gehaltene Aktiven mit Börsenkurs",
  "110": "Forderungen aus Lieferungen und Leistungen",
  "114": "Übrige kurzfristige Forderungen",
  "117": "Steuer-/MWST-Forderungen",
  "120": "Vorräte",
  "130": "Aktive Rechnungsabgrenzungen",
  "140": "Finanzanlagen",
  "144": "Langfristige Forderungen",
  "148": "Beteiligungen",
  "150": "Mobile Sachanlagen",
  "160": "Immobile Sachanlagen",
  "170": "Immaterielle Werte",
  "177": "Goodwill",
  "185": "Nicht einbezahltes Kapital",
  "200": "Verbindlichkeiten aus Lieferungen und Leistungen",
  "210": "Kurzfristige verzinsliche Verbindlichkeiten",
  "220": "Übrige kurzfristige Verbindlichkeiten",
  "230": "Passive Rechnungsabgrenzungen und kurzfristige Rückstellungen",
  "233": "Kurzfristige Rückstellungen",
  "240": "Langfristige verzinsliche Verbindlichkeiten",
  "245": "Darlehen/Hypotheken",
  "250": "Übrige langfristige Verbindlichkeiten",
  "260": "Rückstellungen",
  "280": "Eigenkapital",
  "290": "Reserven und Ergebnis",
  "700": "Nebenerfolg",
  "750": "Nebenerfolg betriebliche Liegenschaft",
  "370": "Eigenleistungen",
  "371": "Eigenverbrauch",
  "380": "Erlösminderungen",
  "390": "Bestandesänderungen",
  "450": "Energieaufwand zur Leistungserstellung",
  "490": "Aufwandminderungen",
  "580": "Übriger Personalaufwand",
  "590": "Leistungen Dritter",
  "610": "Unterhalt/Reparaturen/Leasing mobile Sachanlagen",
  "620": "Fahrzeug- und Transportaufwand",
  "626": "Fahrzeugleasing und -mieten",
  "630": "Sachversicherungen, Abgaben, Gebühren, Bewilligungen",
  "640": "Energie- und Entsorgungsaufwand",
  "650": "Verwaltungsaufwand",
  "657": "Informatikaufwand inkl. Leasing",
  "660": "Werbeaufwand",
  "670": "Sonstiger betrieblicher Aufwand",
  "680": "Abschreibungen und Wertberichtigungen",
  "690": "Finanzaufwand",
  "695": "Finanzertrag",
  "800": "Betriebsfremder Aufwand",
  "810": "Betriebsfremder Ertrag",
  "850": "Ausserordentlicher/periodenfremder Aufwand",
  "851": "Ausserordentlicher/periodenfremder Ertrag",
  "890": "Direkte Steuern",
  "920": "Jahresgewinn/Jahresverlust"
};

function byNumber(a: Account, b: Account): number {
  return Number(a.number) - Number(b.number);
}

function buildRows(data: Account[]): Row[] {
  const rows: Row[] = [];
  const seen = new Set<string>();

  for (const account of [...data].sort(byNumber)) {
    const classCode = account.number.slice(0, 1);
    const group2 = account.number.slice(0, 2);
    const group3 = account.number.slice(0, 3);

    const classKey = `c:${classCode}`;
    if (!seen.has(classKey)) {
      seen.add(classKey);
      rows.push({
        kind: "group",
        key: classKey,
        code: classCode,
        label: CLASS_LABELS[classCode] || account.group || "Klasse",
        level: 1
      });
    }

    const g2Key = `g2:${group2}`;
    if (!seen.has(g2Key)) {
      seen.add(g2Key);
      rows.push({
        kind: "group",
        key: g2Key,
        code: group2,
        label: GROUP_2_LABELS[group2] || "Gruppe",
        level: 2
      });
    }

    const g3Key = `g3:${group3}`;
    if (!seen.has(g3Key)) {
      seen.add(g3Key);
      rows.push({
        kind: "group",
        key: g3Key,
        code: group3,
        label: GROUP_3_LABELS[group3] || "Untergruppe",
        level: 3
      });
    }

    rows.push({ kind: "account", account });
  }

  return rows;
}

export default function AccountsTable({ data, vatMode = "NORMAL" }: { data: Account[]; vatMode?: VatMode }) {
  const rows = buildRows(data);

  function getVatRateLabel(a: Account): string {
    if (vatMode === "NICHT_STEUERPFLICHTIG") return "0.0% (nicht steuerpflichtig)";
    if (vatMode === "PAUSCHAL") return "0.0% (pauschal)";
    if (
      a.calculationTag === "tax_expense" ||
      a.calculationTag === "depreciation" ||
      a.calculationTag === "financial_expense" ||
      a.calculationTag === "financial_income"
    ) {
      return "0.0% (ohne MWST)";
    }
    if (a.calculationTag === "vat_input" || a.calculationTag === "vat_payable") return "0.0% (MWST-Konto)";
    const klass = a.number.slice(0, 1);
    if (klass === "3" || klass === "7" || klass === "8") return "8.1% (Normal)";
    if (klass === "4" || klass === "5" || klass === "6") return "8.1% Vorsteuer";
    return "0.0%";
  }

  return (
    <div className="table-wrap">
      <table className="table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>Nr.</th>
            <th>Name</th>
            <th>Typ</th>
            <th>Modul</th>
            <th>Deklaration</th>
            <th>Berechnung</th>
            <th>MWST-Satz (Default)</th>
            <th>Aktiv</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            if (row.kind === "group") {
              const pad = row.level === 1 ? 8 : row.level === 2 ? 24 : 40;
              return (
                <tr key={row.key}>
                  <td colSpan={8} style={{ fontWeight: 700, background: "rgba(16,39,69,0.06)", paddingLeft: pad, fontSize: 12 }}>
                    {row.code} {row.label}
                  </td>
                </tr>
              );
            }

            const a = row.account;
            return (
              <tr key={a.number}>
                <td>{a.number}</td>
                <td>{a.name}</td>
                <td>{a.type}</td>
                <td>{a.moduleTag || "—"}</td>
                <td>{a.declaration || "—"}</td>
                <td>{a.calculationTag || "—"}</td>
                <td>{getVatRateLabel(a)}</td>
                <td>
                  <span className={a.active ? "pill pill-final" : "pill pill-provisional"}>{a.active ? "JA" : "NEIN"}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
